import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useDialog } from './DialogContext';
import CreateTemplateModal from './CreateTemplateModal';
import FlameIcon from './FlameIcon';
import CollapsibleSection from './team/CollapsibleSection';
import TemplateThumbnail from './templates/TemplateThumbnail';
import { Icon } from './templates/icons';
import { groupByCompany } from './formGrouping';
import { canManageTemplates } from './roles';
import { API_BASE_URL } from './config';
import { PRIMARY_BTN } from './ui';

const NAME_LIMIT = 100;

const boxCount = (template) => template.layout?.boxes?.length || 0;

const TemplatesPage = () => {
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const allowed = canManageTemplates(user.role);
  const isDev = user.role === 'dev';

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesRes, formsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/templates`),
        axios.get(`${API_BASE_URL}/api/forms`),
      ]);
      setTemplates(templatesRes.data);
      setForms(formsRes.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Building templates comes with building forms; everyone else has nothing
    // to do here.
    if (!allowed) {
      navigate('/forms', { replace: true });
      return;
    }
    fetchTemplates();
  }, [allowed, navigate, fetchTemplates]);

  // A new template opens straight in the builder: an empty page is the one
  // thing nobody wants to look at in a list.
  const handleCreateConfirm = async (payload) => {
    setCreateModalOpen(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/templates`, payload);
      navigate(`/templates/${response.data.id}/edit`);
    } catch (error) {
      console.error('Failed to create template:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to create template' });
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/templates`, {
        name: `${template.name} (copy)`.slice(0, NAME_LIMIT),
        form_id: template.form_id,
        layout: template.layout,
      });
      // The copy goes in beneath its original rather than at the far end of
      // the list, where it would have to be hunted for.
      setTemplates((current) => {
        const index = current.findIndex((t) => t.id === template.id);
        const next = [...current];
        next.splice(index + 1, 0, response.data);
        return next;
      });
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to duplicate template' });
    }
  };

  const handleDelete = async (template) => {
    const ok = await confirm({
      title: 'Delete template',
      message: `Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete template',
      danger: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/templates/${template.id}`);
      setTemplates((current) => current.filter((t) => t.id !== template.id));
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert({ title: 'Error', message: 'Failed to delete template. Please try again.' });
    }
  };

  const renderTemplateRow = (template) => {
    const count = boxCount(template);
    return (
      <li key={template.id} className="hover:bg-gray-700/30 transition-colors duration-200">
        <div className="px-6 py-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <button
            type="button"
            onClick={() => navigate(`/templates/${template.id}/edit`)}
            className="flex items-center gap-5 min-w-0 flex-1 bg-transparent hover:bg-transparent p-0 text-left"
            title={`Open ${template.name} in the builder`}
          >
            <TemplateThumbnail layout={template.layout} width={52} />
            {/* Spans throughout: a button may only hold phrasing content. */}
            <span className="block min-w-0 flex-1">
              <span className="block text-lg font-semibold text-white truncate">{template.name}</span>
              {template.form_id ? (
                <span className="mt-1 flex items-center gap-1.5 text-sm text-gray-400 min-w-0">
                  <Icon name="document" className="w-4 h-4 shrink-0 text-purple-300" />
                  <span className="truncate">Filled in from {template.form_name}</span>
                </span>
              ) : (
                <span className="mt-1 flex items-center gap-1.5 text-sm text-amber-300">
                  <Icon name="warning" className="w-4 h-4 shrink-0" />
                  Its form was deleted — open it to choose another
                </span>
              )}
              <span className="block mt-2 text-xs text-gray-500">
                {count} {count === 1 ? 'box' : 'boxes'} · Updated{' '}
                {new Date(template.updated_at || template.created_at).toLocaleDateString()}
              </span>
            </span>
          </button>
          <div className="flex items-center flex-wrap gap-3 lg:ml-4 lg:flex-shrink-0">
            <button
              onClick={() => navigate(`/templates/${template.id}/edit`)}
              className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 shadow-lg"
            >
              Open Builder
            </button>
            <button
              onClick={() => handleDuplicate(template)}
              disabled={!template.form_id}
              title={template.form_id ? 'Make a copy of this template' : 'Choose a form for this template before copying it'}
              className="bg-gray-700/50 hover:bg-gray-600/50 disabled:bg-gray-700/30 disabled:opacity-50 text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border border-gray-600 hover:border-gray-500"
            >
              Duplicate
            </button>
            <button
              onClick={() => handleDelete(template)}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-500/20 hover:border-red-500/40"
            >
              Delete
            </button>
          </div>
        </div>
      </li>
    );
  };

  if (!allowed) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <CreateTemplateModal
        open={createModalOpen}
        forms={forms}
        onConfirm={handleCreateConfirm}
        onCancel={() => setCreateModalOpen(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Templates</h1>
            <p className="text-lg text-gray-400">
              Printable pages that fill themselves in from a form’s answers
            </p>
          </div>
          <div className="mt-4 md:mt-0 md:ml-4">
            <button onClick={() => setCreateModalOpen(true)} className={PRIMARY_BTN}>
              Create New Template
            </button>
          </div>
        </div>

        {/* Templates List */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 px-4">
              <FlameIcon className="mx-auto w-16 h-16 mb-6" />
              <h3 className="text-xl font-semibold text-white mb-2">No templates yet</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                A template is a sheet of paper you lay out once: put each of a form’s answers
                exactly where it belongs, and every submission prints the same way.
              </p>
              <button onClick={() => setCreateModalOpen(true)} className={PRIMARY_BTN}>
                Create Your First Template
              </button>
            </div>
          ) : isDev ? (
            // A dev's list spans every company, so each company's templates sit
            // under a banner of their own that folds them away.
            <div className="divide-y divide-gray-700">
              {groupByCompany(templates).map((group) => (
                <CollapsibleSection
                  key={group.key}
                  tone="group"
                  title={group.companyName}
                  count={group.items.length}
                  countLabel={group.items.length === 1 ? 'template' : 'templates'}
                >
                  <ul className="divide-y divide-gray-700">{group.items.map(renderTemplateRow)}</ul>
                </CollapsibleSection>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-gray-700">{templates.map(renderTemplateRow)}</ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
